/**
 * Portal Dashboard - World Class Minimalist Design
 * Apple meets ChatGPT meets Lexy
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, ChevronRight, LogOut, Sparkles, MessageSquare,
  FileText, Clock, Loader2, X, Send
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

interface PortalCase {
  id: string;
  title: string;
  status: string;
  status_label: string;
  lawyer_name: string;
  created_at: string;
  updated_at: string;
  next_deadline?: string;
  unread_messages: number;
  documents_count: number;
}

const PortalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<PortalCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<any>(null);
  const [branding, setBranding] = useState<any>(null);
  const [showNewCase, setShowNewCase] = useState(false);
  const [newCaseTitle, setNewCaseTitle] = useState('');
  const [newCaseDescription, setNewCaseDescription] = useState('');
  const [creating, setCreating] = useState(false);
  
  useEffect(() => {
    const storedClient = localStorage.getItem('portal_client');
    const storedBranding = localStorage.getItem('portal_branding');
    
    if (storedClient) setClient(JSON.parse(storedClient));
    if (storedBranding) setBranding(JSON.parse(storedBranding));
    
    fetchCases();
  }, []);
  
  const fetchCases = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API}/api/portal/my/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data || []);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_client');
    localStorage.removeItem('portal_branding');
    navigate('/portal/login');
  };
  
  const createNewCase = async () => {
    if (!newCaseTitle.trim()) return;
    
    setCreating(true);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.post(
        `${API}/api/portal/my/cases`,
        {
          title: newCaseTitle,
          description: newCaseDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Case request submitted');
      setShowNewCase(false);
      setNewCaseTitle('');
      setNewCaseDescription('');
      fetchCases();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Could not create case');
    } finally {
      setCreating(false);
    }
  };
  
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'bg-blue-500';
      case 'WAITING_CLIENT': return 'bg-amber-500';
      case 'COMPLETED': return 'bg-emerald-500';
      case 'PENDING': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };
  
  const totalUnread = cases.reduce((sum, c) => sum + (c.unread_messages || 0), 0);
  const firstName = client?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-50/30 via-transparent to-indigo-50/30 pointer-events-none" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{branding?.firm_name || 'Client Portal'}</p>
                <p className="text-xs text-gray-400">{client?.company_name || client?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {totalUnread > 0 && (
                <div className="px-2.5 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                  {totalUnread} new
                </div>
              )}
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-3xl mx-auto px-6 py-8 relative">
        {/* Welcome */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">
            Hi, {firstName}
          </h1>
          <p className="text-gray-500 mt-1">
            Track your cases and communicate with your lawyer
          </p>
        </motion.div>
        
        {/* Quick actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <button
            onClick={() => setShowNewCase(true)}
            className="w-full p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Request new case</p>
                <p className="text-sm text-gray-500">Describe your legal matter</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 ml-auto group-hover:text-gray-500 group-hover:translate-x-1 transition-all" />
            </div>
          </button>
        </motion.div>
        
        {/* Cases */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
              Your Cases
            </h2>
            <span className="text-sm text-gray-400">{cases.length} total</span>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
            </div>
          ) : cases.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl p-12 text-center border border-gray-100"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500">No cases yet</p>
              <p className="text-sm text-gray-400 mt-1">Click above to request a new case</p>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {cases.map((caseItem, index) => (
                <motion.div
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/portal/case/${caseItem.id}`)}
                  className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100 transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-4">
                    {/* Status indicator */}
                    <div className={`w-2 h-2 rounded-full mt-2 ${getStatusStyle(caseItem.status)}`} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900 group-hover:text-violet-600 transition-colors">
                            {caseItem.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {caseItem.lawyer_name} · {caseItem.status_label}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-3">
                        {caseItem.unread_messages > 0 && (
                          <div className="flex items-center gap-1.5 text-blue-600">
                            <MessageSquare className="w-4 h-4" />
                            <span className="text-sm font-medium">{caseItem.unread_messages} new</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{caseItem.documents_count}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">
                            {format(new Date(caseItem.updated_at), 'MMM d')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* New Case Modal */}
      <AnimatePresence>
        {showNewCase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowNewCase(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">New Case Request</h2>
                    <p className="text-sm text-gray-500 mt-1">Tell us about your legal matter</p>
                  </div>
                  <button
                    onClick={() => setShowNewCase(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={newCaseTitle}
                    onChange={(e) => setNewCaseTitle(e.target.value)}
                    placeholder="e.g., Contract review for new partnership"
                    className="w-full h-12 px-4 bg-gray-50 rounded-xl border-0 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newCaseDescription}
                    onChange={(e) => setNewCaseDescription(e.target.value)}
                    placeholder="Describe your legal matter in detail..."
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-50 rounded-xl border-0 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-all resize-none"
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-6 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setShowNewCase(false)}
                  className="flex-1 h-12 bg-white text-gray-700 rounded-xl font-medium border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={createNewCase}
                  disabled={!newCaseTitle.trim() || creating}
                  className="flex-1 h-12 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-200 text-white disabled:text-gray-400 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Footer */}
      <footer className="max-w-3xl mx-auto px-6 py-8 text-center">
        <p className="text-xs text-gray-400">
          Powered by Lexy · Your secure client portal
        </p>
      </footer>
    </div>
  );
};

export default PortalDashboard;
